/* eslint-disable max-len */
// #region Imports
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, Renderer2, TemplateRef, ElementRef, ViewChild, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';

import { of, Subscription, delay, Observable, combineLatest, filter } from 'rxjs';
import { Store } from '@ngrx/store';


// Selectors
import { selectActiveProject } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';


// Models
import {
  DataSchemaWithRelations, ImproveProjectDescriptionByAiResponseDto, Project,
  ProjectControllerService, ProjectModelField,
  ProjectModelRelation, ProjectModelWithRelations,
  ScopeOfWork,
  PageWithRelations,
  ProjectTheme,
  ComponentDefinitionWithRelations,
  UiDataStore,
  Page,
  MessageTemplate,
  User,
  Person,
  Tenant,
  ModuleWithRelations,
  ClientLocalState,
  ProcessInfoWithRelations,
  ProjectModelEndpoint
} from '@rappider/rappider-sdk';


// Enums
import { PostItColor, PostItSize } from 'libs/components/src/lib/components/post-it/post-it.module';

// Configs
import {
  ProjectDesignSettingButtons
} from './configs/action-button-config';
import { SetAsActiveProjectTheme, UpdateProject } from 'libs/project/src/lib/states/projects-state/project.actions';

import { selectIsProjectModelsLoaded, selectProjectModels } from 'libs/project/src/lib/states/project-model-state/project-model.selectors';
import { selectProjectModelRelations } from 'libs/project/src/lib/states/project-model-relation-state/project-model-relation.selectors';
import { selectMessageTemplates } from 'libs/project/src/lib/states/message-template-state/message-template.selectors';
import { selectUser } from 'libs/authentication/src/lib/state/authentication.selectors';
import { selectClientLocalStateLoaded, selectClientLocalStates } from 'libs/shared/src/lib/states/client-local-state/client-local-state.selectors';

import { Deploy, GenerateCode, GetProjectSourceCode, GetProjectSourceFileCustomCodes, GetProjectSourceFiles } from 'libs/project-source-code/src/lib/project-source-code-data-source/project-source-code.actions';

import { ActionResponse, CardOneComponentConfig, CrudFormMonacoCodeEditorItem, HeadingType, IconType, ItemPerRow, NgZorroIconTheme, SelectableOption, ButtonSize, TimelineItem, CardOneListCardClickOutput } from '@rappider/rappider-components/utils';
import {
  DeleteProjectModelField
} from 'libs/project/src/lib/states/project-model-state/project-model.actions';
import { selectAllProjectModelFields } from 'libs/project-model-field/src/lib/state/project-model-field.selectors';
import { AIProgressStatus, WIZARD_STEPS_CONFIG, WizardStepConfig, WizardSteps, WizardStepStatus } from './configs/wizard-steps';

import { NotificationService } from 'libs/components/src/lib/services';
import { cloneDeep, orderBy } from 'lodash';
import { COLLAPSE_PANEL_CONFIGS, CollapsePanelConfig, CollapsePanels } from './configs/collapse-panel-config';
import { projectDescriptionStepCompletedContent, serverSideBusinessProcessesStepIntroductionExtraContent } from './configs/wizard-steps-content';


import { getComponentDefinitionsWithDetailsSelector, Navigate } from '@rappider/shared';
import { ContentTree } from 'libs/content-tree-renderer/src/lib/models';

import { PROJECT_HOME_FEATURES_CONFIG } from '@rappider/shared/configs';
import { FullPaths, PATH_DEFINITIONS, QUERY_PARAM_DEFINITIONS } from '@rappider/shared/definitions';
import { MESSAGE_TEMPLATES_EXPLANATION } from './configs/content';
import { pageCardsConfig, layoutCardsConfig, messageTemplatesCardsConfig, themeCardsConfig, projectVersionCardsConfig, dataModelCardsConfig, endpointsCardsConfig, projectSettingsCardsConfig, projectEnvironmentCardsConfig, uiDataStoreCardsConfig, projectMembersCardsConfig, shareApplicationCardsConfig, deployCardsConfig, serverCardsConfig, serverSideBusinessProcessesCardsConfig, dataModelRelationsCardsConfig } from './configs/cards-config';
import { ChangeDefaultLayout } from 'libs/pages/src/lib/state/layout-state/layout.actions';
import { NavigatePages } from './configs/navigate-pages.enum';
import { ProjectDesignService } from './services/project-design.service';
import { CreateClientLocalState } from 'libs/shared/src/lib/states/client-local-state/client-local-state.actions';

import { ClientLocalStateKeys } from 'libs/shared/src/lib/states/client-local-state/client-local-state.interface';
import { TimelineItemColor } from 'libs/components/src/lib/utils/timeline/timeline-item-color.enum';
import { NextStep } from './state/project-design-task/project-design-task.actions';
import { FocusToRelatedModelId } from 'libs/db-diagram/src/lib/state/db-diagram.actions';
import { FocusToRelatedDataStoreId } from 'libs/diagram-editor/src/lib/state/diagram-state/diagram.actions';
import { LayoutMode } from './configs/layoutMode.enum';
import { DeployStatus } from 'libs/project/src/lib/components/live-preview/utils/deploy-status.enum';
import { SyncPriorityRemoteData, SyncSecondaryRemoteData } from 'apps/rappider/src/app/state/app.actions';
import { ScrollTriggerService } from 'libs/project/src/lib/services/scroll-trigger.service';
import { CodeNavigationService } from 'libs/project/src/lib/services/code-navigation.service';
import { SelectTabService } from 'libs/project/src/lib/services/select-tab.service';
import { ButtonTriggerService } from 'libs/project/src/lib/services/button-trigger.service';
import { StartApplication, StopApplication } from 'libs/project-source-code/src/lib/project-source-code-data-source/project-source-code.actions';
import { AI_CHAT_CONFIGS } from '../../../ai-integration/src/lib/configs/ai-chat-config';
import { AIGenerationType } from '@rappider/ai-integration';
import { isEveryDataFetched } from 'libs/workflow-diagram/src/lib/utils/workflow-function-event.selector';


const autoCollapsedPanels = [
  CollapsePanels.ProjectDescription,
  CollapsePanels.ProjectInformation,
  CollapsePanels.ProjectMembers,
  CollapsePanels.ScopeOfWork,
  CollapsePanels.DataModelsAndDatabase,
  CollapsePanels.APIEndpoints,
  CollapsePanels.UIPages,
  CollapsePanels.UIPageLayouts,
  CollapsePanels.Theme,
  CollapsePanels.DesignSystem,
  CollapsePanels.UIBusinessProcesses,
  CollapsePanels.MessageTemplates,
  CollapsePanels.ServerSideBusinessProcesses,
  CollapsePanels.SettingsVariablesAndEnvironments,
  CollapsePanels.GenerateProjectCode,
  // CollapsePanels.VersionsAndDeployments
];

// #endregion
@Component({
  selector: 'rappider-project-design',
  templateUrl: './project-design.smart.component.html',
  styleUrls: ['./project-design.smart.component.scss'],
})
export class ProjectDesignComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() displayWizardOnIntroduction? = false;
  @Input() isLoading? = false;
  @Input() wizardStepsConfigs: WizardStepConfig[] = [];
  @Input() activeWizardStep?: number | undefined = undefined;
  @Input() displayWizard?: boolean = true;

  @Output() collapseButtonClicked = new EventEmitter<string>();
  @Output() layoutModeChanged = new EventEmitter<string>();
  @Output() displayCodePanelClicked = new EventEmitter<string>();

  @ViewChild('wizardContainer', { static: false }) wizardContainer!: ElementRef;

  // #region Variables

  @ViewChild('stepNotStarted', { static: true }) stepNotStarted!: TemplateRef<any>;
  @ViewChild('stepInProgress', { static: true }) stepInProgress!: TemplateRef<any>;
  @ViewChild('stepCompleted', { static: true }) stepCompleted!: TemplateRef<any>;
  @ViewChild('stepOptional', { static: true }) stepOptional!: TemplateRef<any>;
  @ViewChild('stepError', { static: true }) stepError!: TemplateRef<any>;
  @ViewChild('stepNoStatus', { static: true }) stepNoStatus!: TemplateRef<any>;

  subscriptions: Subscription[] = [];
  // set the flag to true when all subscriptions are populated
  allSubscriptionsPopulated = false;

  latestRenderedStep: number | undefined = 0;
  latestRenderedStatus: WizardStepStatus | undefined = WizardStepStatus.NotStarted;

  // projectDesignTasks: ProjectDesignTask[] = [];

  collapsePanelConfigs: CollapsePanelConfig = cloneDeep(COLLAPSE_PANEL_CONFIGS);

  // subscription based entity data
  activeProject: Project | undefined;

  MESSAGE_TEMPLATES_EXPLANATION = MESSAGE_TEMPLATES_EXPLANATION;

  clickedLayoutCardId: string | undefined;
  clickedThemeCardId: string | undefined;

  scopeOfWorks: ScopeOfWork[] = [];
  scopeOfWorkStatuses: any = {};
  projectModels: ProjectModelWithRelations[] = [];
  projectModelRelations: ProjectModelRelation[] | undefined;
  projectModelFields: ProjectModelField[] | undefined;
  dataSchemas: DataSchemaWithRelations[] | undefined;
  messageTemplates: any[] | undefined;
  projectVersions: any[] | undefined;
  projectVersionsLength: number | undefined;
  contentTree: ContentTree | undefined;
  componentDefinitions: ComponentDefinitionWithRelations[] | undefined;
  uiDataStores: UiDataStore[] | undefined;
  page: PageWithRelations | undefined;
  layout: Page | undefined;
  projectTheme: ProjectTheme | any;
  pages: Page[] | undefined;
  layouts: Page[] | undefined;
  projectThemes: ProjectTheme[] | undefined;
  uiPageCards: CardOneComponentConfig[] | undefined = [];
  activeLayout: PageWithRelations | null | undefined;
  user: User | undefined;
  person: Person | undefined;
  tenant: Tenant | undefined;

  projectModelsLoaded = false;

  grouppedCards: SelectableOption[] | undefined;
  modules: ModuleWithRelations[] | undefined;
  previousLayoutId: string | undefined;
  previousThemeId: string | undefined;

  latestProjectVersionId: string | undefined;

  // imported enums and constants
  CollapsePanels = CollapsePanels;
  WizardStepStatus = WizardStepStatus;
  WizardSteps = WizardSteps;
  AIProgressStatus = AIProgressStatus;
  PostItColor = PostItColor;
  PostItSize = PostItSize;
  ProjectDesignSettingButtons = ProjectDesignSettingButtons;
  ItemPerRow = ItemPerRow;
  HeadingType = HeadingType;
  workflowEvents = [];
  workflowStepFunctions = [];
  subscribedEvents = [];
  publishedEventsOnSuccess = [];
  publishedEventsOnFailure = [];
  starterNodes = [];

  pageCardsConfig = cloneDeep(pageCardsConfig);
  layoutCardsConfig = cloneDeep(layoutCardsConfig);
  themeCardsConfig = cloneDeep(themeCardsConfig);
  projectVersionCardsConfig = cloneDeep(projectVersionCardsConfig);
  dataModelCardsConfig = cloneDeep(dataModelCardsConfig);
  dataModelRelationsCardsConfig = cloneDeep(dataModelRelationsCardsConfig);
  messageTemplatesCardsConfig = cloneDeep(messageTemplatesCardsConfig);
  endpointsCardsConfig = cloneDeep(endpointsCardsConfig);
  projectSettingsCardsConfig = cloneDeep(projectSettingsCardsConfig);
  projectEnvironmentCardsConfig = cloneDeep(projectEnvironmentCardsConfig);
  uiDataStoreCardsConfig = cloneDeep(uiDataStoreCardsConfig);
  serverSideBusinessProcessesCardsConfig = cloneDeep(serverSideBusinessProcessesCardsConfig);
  projectMembersCardsConfig = cloneDeep(projectMembersCardsConfig);
  shareApplicationCardsConfig = cloneDeep(shareApplicationCardsConfig);
  deployCardsConfig = cloneDeep(deployCardsConfig);
  serverCardsConfig = cloneDeep(serverCardsConfig);
  // others
  projectModelCreationIsCompleted = false;

  rapiderAiWizardVisible = true;
  // shows a footer when the wizard is outside the viewport
  showWizardOnBluePrintFooter = true;

  projectDescriptionEditMode = false;

  /* active - visible collapse panel keys as string array */
  visiblePanelKeys: string[] = [];

  // next button is disabled when the user is not allowed to navigate to the next step
  // disableNextButton = false;

  /* True; If all collapse panels are opened - active */
  allPanelsActive = false;

  /* Sets as true/false when the crud emits the status of the collapse */
  projectInformationCollapseActive = true;

  /* Passes to user invitation component to show invitation modal  */
  showInvitationModal = '';

  /* variables to show models with a delayed animation */
  displayModelFieldsWithAnimation = false;

  // menu items for low-code customization pages
  projectMegaMenuFeatures = PROJECT_HOME_FEATURES_CONFIG;
  megaMenuVisible = false;

  // record set for the sub collapse panel visibility
  isSubCollapsePanelVisible: Record<string, any> = {};

  displayBlueprint = true;
  leftSideContainerWidth = 740;

  // active code path
  activeCodePath = '';

  // active design path
  activeDesignPath = '';
  messageTemplateIsLoaded = false;
  pagesIsLoaded = false;
  pageLayoutIsLoaded = false;
  activeProjectThemeIsLoaded = false;
  activeNavigationType: NavigatePages | undefined;

  // show animation next endpoint model
  showNextEndpointModel = false;
  currentEndpointIndex = 0;
  isEndpointScrolling = false;
  currentEndpoints: any[] = [];

  clientLocalState: ClientLocalState[] | undefined | null;

  projectDescriptionStep = WIZARD_STEPS_CONFIG.find(step => step.key === WizardSteps.ProjectDescription);

  layoutMode: string | undefined;

  aiStatusProjectDescriptionImproved: AIProgressStatus | undefined;

  isSocialMediaShared = false;

  wizardStepsTimelineItems: TimelineItem[] = [];
  displayWizardNextButton = false;
  deployStatus: DeployStatus = DeployStatus.NotStarted;
  DeployStatus = DeployStatus;
  isProjectCodeGenerationCompleted = false;

  // deployment information list
  deployInfos: any[] = [];

  serverSideBusinessProcessesStepIntroductionExtraContent = serverSideBusinessProcessesStepIntroductionExtraContent;

  environments: string[] = [];

  // #endregion

  // #region Constructor
  constructor(
    private store: Store<any>,
    private renderer: Renderer2,
    private projectApi: ProjectControllerService,
    private notificationService: NotificationService,
    private projectDesignService: ProjectDesignService,
    private scrollTriggerService: ScrollTriggerService,
    private codeNavigationService: CodeNavigationService,
    private selectTabService: SelectTabService,
    private buttonTriggerService: ButtonTriggerService,
    private router: Router
  ) {
    console.log('project design constructed');
  }
  // #endregion

  // #region Lifecycle Hooks

  ngOnInit(): void {
    this.subscribeToInitialData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.activeWizardStep && changes.activeWizardStep.currentValue !== undefined) ||
      (changes.wizardStepsConfigs && changes.wizardStepsConfigs.currentValue !== undefined)
    ) {
      this.renderStep();
    }
  }

  ngAfterViewInit(): void {
    // If the wizard is outside the viewport, show the footer
    // this.observeWizardFooterVisibility();
    console.log('project design after view init');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  // #endregion

  // #region Subscriptions

  subscribeToInitialData() {
    // Subscribe to user and active project
    this.subscriptions = [
      this.subscribeToUser(),
      this.subscribeToActiveProject(),
    ];
  }

  subscribeToRemainingData() {
    // Subscribe to project design tasks
    if (!this.allSubscriptionsPopulated) {
      this.allSubscriptionsPopulated = true;
      this.subscriptions.push(...[
        this.subscribeToClientLocalState(),

        /* entities */
        this.subscribeToProjectModels(),
        this.subscribeToProjectModelRelations(),
        // this.subscribeToDataSchemas(),
        // this.subscribeToScopeOfWork(),
        this.subscribeToMessageTemplates(),
        // this.subscribeToMessageTemplatesIsLoaded(),
        this.subscribeToProjectVersions(),
        /* pages */
        // this.subscribeToContentTree(),
        this.subscribeToComponentDefinitions(),
        this.subscribeToUIDataStores(),
        this.subscribeToModulesAndPages(),
        // this.subscribeToPagesIsLoaded(),
        this.subscribeToPageLayouts(),
        this.subscribeToProjectTheme(),
        this.subscribeToDeployManagementInformation(),
        this.subscribeToProcessInfos(),
        this.subscribeToProjectCodeGeneration(),
        this.subscribeToScrollToEndpointCode(),
        this.subscribeToEnvironmentVariables(),
        this.subscribeToDiagramData()
      ]);
    }
  }

  subscribeToUser() {
    return this.store.select(selectUser).subscribe(user => {
      if (user) {
        this.user = user;
        this.person = user?.people?.length ? user.people[0] : undefined;
        this.tenant = this.person?.tenant;
      }
    });
  }

  subscribeToActiveProject() {
    return this.store.select(selectActiveProject).subscribe(activeProject => {
      if (activeProject?.id) {
        if (activeProject?.id && this.activeProject?.id && this.activeProject?.id !== activeProject?.id) {
          this.resetProjectDataToLoadAnotherProject();
        }
        const themeChanged = this.activeProject?.activeProjectThemeId !== activeProject?.activeProjectThemeId;
        this.activeProject = activeProject;

        if (this.activeProject?.activeProjectThemeId) {
          this.activeProjectThemeIsLoaded = true;
        }
        if (themeChanged && activeProject?.activeProjectThemeId) {
          this.handleThemeChange(activeProject.activeProjectThemeId);
        }

        if (activeProject?.id) {
          this.subscribeToRemainingData();
          this.renderStep();
        }
      } else {
        this.activeProject = undefined;
        this.projectModelsLoaded = false;
        console.log('No active project');
      }
    });
  }

  resetProjectDataToLoadAnotherProject() {
    this.projectModelsLoaded = false;
    this.activeCodePath = '';
    this.activeDesignPath = '';
    this.projectVersions = [];
    this.latestProjectVersionId = undefined;
  }

  subscribeToClientLocalState() {
    // Trigger effect to read data from IndexDB and populate the state
    return combineLatest([
      this.store.select(selectClientLocalStates),
      this.store.select(selectClientLocalStateLoaded)
    ]).subscribe(([clientLocalState, isLoaded]) => {
      if (isLoaded) {
        this.clientLocalState = cloneDeep(clientLocalState);

        // when we complete the project description improvement step, we save it to the client local state
        const projectDescriptionImproved = this.clientLocalState?.find((cls: ClientLocalState) => cls.key === ClientLocalStateKeys.AiStatusProjectDescriptionImproved);
        this.aiStatusProjectDescriptionImproved = projectDescriptionImproved?.value;

        // when we complete the social media sharing step, we save it to the client local state
        const socialMediaShared = this.clientLocalState?.find((cls: ClientLocalState) => cls.key === ClientLocalStateKeys.SocialMediaShared);
        this.isSocialMediaShared = socialMediaShared?.value === true;

      }
    });
  }

  subscribeToProjectModels() {
    return combineLatest([
      this.store.select(selectIsProjectModelsLoaded),
      this.store.select(selectProjectModels),
      this.store.select(selectAllProjectModelFields)
    ]).subscribe(([isProjectModelsLoaded, projectModels, projectModelFields]) => {
      this.projectModelFields = projectModelFields ? projectModelFields : [];
      this.projectModelsLoaded = !!isProjectModelsLoaded;
      if (projectModels?.length) {
        this.projectModels = projectModels.map((projectModel: ProjectModelWithRelations) => <ProjectModelWithRelations>{
          ...projectModel,
          fields: orderBy(projectModelFields.filter((field) => field.projectModelId === projectModel?.id), ['index'], ['asc'])
        });
        // console.log('Project Models:', this.projectModels);

        if (this.projectModels?.length) {
          this.projectModelsLoaded = true;
          this.mapEndpointItems();
          this.mapDataModelItems();
        }
      }
    });
  }

  subscribeToScrollToEndpointCode() {
    return this.codeNavigationService.codeNavigationTrigger$.subscribe(
      (endpoint: ProjectModelEndpoint) => {
        this.onEndpointRowActionClick(endpoint);
      });
  }

  subscribeToProjectModelRelations() {
    return this.store.select(selectProjectModelRelations).subscribe((projectModelRelations: ProjectModelRelation[]) => {
      this.projectModelRelations = projectModelRelations ? projectModelRelations : [];
      if (this.projectModelRelations?.length) {
        this.mapDataModelRelationItems();
      }
      console.log('Project Model Relations:', projectModelRelations);
    });
  }

  subscribeToMessageTemplates() {
    return this.store.select(selectMessageTemplates).subscribe((messageTemplates: MessageTemplate[]) => {
      this.messageTemplates = messageTemplates ? messageTemplates : [];
      if (messageTemplates?.length) {
        this.mapMessageTemplateItems();
      }
    });
  }

  subscribeToComponentDefinitions() {
    return this.store
      .select(<any>getComponentDefinitionsWithDetailsSelector)
      .subscribe((componentDefinitions: ComponentDefinitionWithRelations[]) => {
        this.componentDefinitions = cloneDeep(componentDefinitions);
      });
  }

  subscribeToUIDataStores() {
    return this.store
      .select((state) => state.uiDataStore?.data)
      .subscribe((uiDataStores: UiDataStore[]) => {
        this.uiDataStores = uiDataStores;
        if (this.uiDataStores?.length) {
          this.mapUIDataStoreItems();
        }
      });
  }

  subscribeToPageLayouts() {
    return this.store.select(state => state.layout?.data).subscribe(layouts => {
      this.layouts = layouts;

      if (this.layouts?.length) {
        const defaultLayout = this.layouts.find(layout => layout.isDefault === true);
        if (defaultLayout) {
          this.activeLayout = defaultLayout;
          this.clickedLayoutCardId = defaultLayout.id;
        }

        this.pageLayoutIsLoaded = true;
        this.mapLayoutItems();
      }
    });
  }

  subscribeToProjectTheme() {
    return this.store
      .select(state => state.projectTheme?.data)
      .subscribe((projectThemes: ProjectTheme[]) => {
        if (projectThemes?.length) {
          this.projectThemes = projectThemes;
          const themeId = this.clickedThemeCardId || this.activeProject?.activeProjectThemeId;
          const activeTheme = themeId ? projectThemes.find(theme => theme.id === themeId) : projectThemes[0];

          if (activeTheme) {
            this.projectTheme = {
              ...this.projectTheme,
              ...(activeTheme?.theme ?? {}),
              ...(activeTheme?.customTheme ?? {}),
              id: activeTheme.id,
              name: activeTheme.name
            };
            if (!this.clickedThemeCardId || this.clickedThemeCardId !== activeTheme.id) {
              this.clickedThemeCardId = activeTheme.id;
            }
            this.previousThemeId = activeTheme.id;

            this.mapThemeItems();
          }
        }
      });
  }

  subscribeToDeployManagementInformation() {
    return this.store.select(state => state.deployManagement)
      .subscribe((deployManagement: any) => {
        if (deployManagement) {
          if (deployManagement.deployInfo?.length) {
            this.deployInfos = deployManagement.deployInfo;
            this.deployStatus = deployManagement.deployInfo[0].status;
          } else {
            this.deployInfos = [];
            this.deployStatus = DeployStatus.NotStarted;
          }

          this.updateServerCardStatus(deployManagement);
          this.mapDeployProjectItems();
        }
      });
  }


  subscribeToProcessInfos() {
    return this.store.select(state => state.processInfo.data).subscribe((processInfos: ProcessInfoWithRelations[]) => {
      if (processInfos?.length && !this.projectModelCreationIsCompleted) {
        const aiOrchestrationProjectModel = processInfos.find(processInfo => processInfo.identifier === 'ai-orchestration-project-model');
        const aiOrchestrationProjectModelRelation = processInfos.find(processInfo => processInfo.identifier === 'ai-orchestration-project-model-relation');
        if (aiOrchestrationProjectModel?.status === 'completed' && aiOrchestrationProjectModelRelation?.status === 'completed') {
          this.projectModelCreationIsCompleted = true;
        }
      }
    });
  }

  subscribeToProjectCodeGeneration() {
    return this.store.select(state => state.projectCodeGeneration?.data)
      .subscribe(projectCodeGenerations => {
        if (projectCodeGenerations?.length) {
          this.isProjectCodeGenerationCompleted = projectCodeGenerations.some((projectCodeGeneration: any) => projectCodeGeneration.status === 'successful');
        }
      });
  }

  subscribeToProjectVersions() {
    return this.store.select(state => state.projectVersion.data).subscribe(projectVersions => {
      this.projectVersions = projectVersions;
      if (this.projectVersions?.length && projectVersions?.length !== this.projectVersionsLength) {
        const latestVersions = orderBy(this.projectVersions, 'createdDate', 'desc');
        this.latestProjectVersionId = latestVersions[0].id;
        if (this.latestProjectVersionId) {
          this.projectVersionsLength = projectVersions?.length;
        }
      }
      this.mapProjectVersionItems();
    });
  }

  subscribeToEnvironmentVariables() {
    return this.store.select(state => state.environmentVariable?.environments).subscribe((environments) => {
      if (environments?.length) {
        this.environments = environments;
        this.mapProjectEnvironmentItems();
      }
    });
  }

  subscribeToModulesAndPages() {
    return combineLatest([
      this.store.select(state => state.module?.data),
      this.store.select(state => state.page?.data)
    ]).subscribe(([modules, pages]) => {
      this.modules = modules;
      this.pages = pages;

      if (this.modules?.length && this.pages?.length) {
        this.mapPageItems();
      }
    });
  }

  // #endregion

  // #region Wizard Navigation and Step Specific Logic

  onWizardNextStepButtonClicked() {
    // this.store.dispatch(NextStep({ payload: { activeProjectId: this.activeProject?.id ?? '' } }));
  }

  onWizardSkipStepsButtonClicked() {
    console.log('Wizard skip step button clicked');
    // this.store.dispatch(NextStep({ payload: { activeProjectId: this.activeProject?.id ?? '' } }));
  }

  scrollToActiveSectionCollapsePanel() {
    /* scroll to the section */
    setTimeout(() => {
      if (this.activeWizardStep !== undefined && this.wizardStepsConfigs[this.activeWizardStep]) {
        this.scrollToDomElement(this.wizardStepsConfigs[this.activeWizardStep].collapsePanel || '');
      }
    }, 250);
  }

  isStepVisible(stepNumber: number): boolean {
    if (this.activeWizardStep === undefined) {
      return false;
    }
    return (stepNumber <= this.activeWizardStep + 1) &&
      (
        this.wizardStepsConfigs[stepNumber - 1].status === WizardStepStatus.InProgress ||
        this.wizardStepsConfigs[stepNumber - 1].status === WizardStepStatus.Completed
      );
  }

  getStepStatusTemplate(key: WizardSteps): TemplateRef<any> {
    if (this.activeProject?.isCreatedByAI) {
      const status = this.wizardStepsConfigs.find(step => step.key === key)?.status || WizardStepStatus.NotStarted;

      switch (status) {
        case WizardStepStatus.NotStarted:
          return this.stepNotStarted;
        case WizardStepStatus.InProgress:
          return this.stepInProgress;
        case WizardStepStatus.Completed:
          return this.stepCompleted;
        case WizardStepStatus.Optional:
          return this.stepOptional;
        case WizardStepStatus.Error:
          return this.stepError;
        default:
          return this.stepNotStarted;
      }
    } else {
      return this.stepNoStatus;
    }
  }

  getStepStatusTemplateForDeployment() {
    return this.stepNoStatus;
  }

  // Renders the wizard and step content based on the active step
  renderStep() {

    if (this.activeProject?.id && this.activeWizardStep !== undefined && this.wizardStepsConfigs[this.activeWizardStep] &&
      (
        this.latestRenderedStep !== this.activeWizardStep ||
        (this.latestRenderedStep === this.activeWizardStep &&
          this.latestRenderedStatus !== this.wizardStepsConfigs[this.activeWizardStep]?.status
        )
      )
    ) {

      this.mapWizardStepsTimelineItems();

      // refresh the source code list
      this.getProjectSourceCodeFiles();

      // memorize the last rendered step
      this.latestRenderedStep = this.activeWizardStep;
      this.latestRenderedStatus = this.wizardStepsConfigs[this.activeWizardStep].status;

      // update visibility of the collapse panel IF the planning is not completed
      if (!this.isProjectDesignPlanningCompleted()) {
        if ((this.wizardStepsConfigs?.length - 1) !== this.activeWizardStep) {
          this.onCollapsePanelActiveChange(this.wizardStepsConfigs[this.activeWizardStep].collapsePanel, true);
        }

        if (this.wizardStepsConfigs && this.wizardStepsConfigs[this.activeWizardStep].status === WizardStepStatus.Introduction &&
          this.wizardStepsConfigs[this.activeWizardStep - 1]) {
          this.onCollapsePanelActiveChange(this.wizardStepsConfigs[this.activeWizardStep - 1].collapsePanel, true);
        }
      }

      this.scrollToActiveSectionCollapsePanel();

      // // trigger next step if the current step is in progress and max wait time has passed
      // if (this.wizardStepsConfigs[this.activeWizardStep]?.status === WizardStepStatus.InProgress) {
      //   this.triggerNextWizardStep();
      // }
    }

  }

  mapWizardStepsTimelineItems() {
    const notStartedIcon = 'fa-regular fa-square';
    const inProgressIcon = 'fa-solid fa-spinner-third fa-spin';
    const completedIcon = 'fa-solid fa-check-square';
    if (this.wizardStepsConfigs?.length) {
      this.wizardStepsTimelineItems = this.wizardStepsConfigs?.filter(i => i.projectDesignTaskIdentifier)?.map((step: WizardStepConfig) => <TimelineItem>{
        title: step.planTitle,
        color: step.status === WizardStepStatus.Completed ? TimelineItemColor.Success : step.status === WizardStepStatus.NotStarted ? TimelineItemColor.Disabled : TimelineItemColor.InProgress,
        icon: {
          type: IconType.FontAwesome,
          name: step.status === WizardStepStatus.Completed ? completedIcon : step.status === WizardStepStatus.InProgress ? inProgressIcon : notStartedIcon,
          color: step.status === WizardStepStatus.Completed ? TimelineItemColor.Success : step.status === WizardStepStatus.InProgress ? TimelineItemColor.InProgress : TimelineItemColor.Disabled,
          size: '20px',
          customColorSettings: {
            backgroundColor: 'var(--alternative-background-color)',
          },
          borderSettings: {
            borderColor: 'var(--alternative-background-color)',
          }
        },
        markdownContent: step.status === WizardStepStatus.InProgress ? {
          typeInRealTime: true,
          typingSpeed: 1,
          markdownText: step.description
        } : undefined
      });
    }
  }

  shouldProjectDescriptionTypeInRealTime(): boolean {
    const projectDescriptionStepIndex = 1;
    if (this.activeProject?.description && this.activeWizardStep !== undefined && this.activeWizardStep <= projectDescriptionStepIndex) {
      if (this.isStepVisitedForTheFirstTime(WizardSteps.ProjectDescription)) {
        return true;
      }
    }
    return false;
  }

  setStepAnimations() {
    // Step specific actions, step 0 is the welcome step, subtract 1 from the step number on the wizard
    const activeStep = this.activeWizardStep !== undefined ? this.wizardStepsConfigs[this.activeWizardStep] ?? null : null;
    switch (activeStep?.key) {
      // Project Description
      case WizardSteps.ProjectDescription:
        // const isProjectDescriptionImproved = this.clientLocalState?.find((cls: ClientLocalState) => cls.key === ClientLocalStateKeys.aiStatusProjectDescriptionImproved);
        // if (isProjectDescriptionImproved?.value) {
        //   this.enableAndHighlightNextButton();
        //   activeStep.description = projectDescriptionStepCompletedContent;
        // }
        break;

      //  Data Models
      case WizardSteps.DataModelsAndDatabase:
        break;

      // API Endpoints
      case WizardSteps.APIEndpoints:
        break;

      // UI Pages
      case WizardSteps.UIPages:
        break;

      default:
        break;
    }

    if (this.shouldProjectDescriptionTypeInRealTime() && this.collapsePanelConfigs[CollapsePanels.ProjectDescription]?.crudForm?.items[0]) {
      (this.collapsePanelConfigs[CollapsePanels.ProjectDescription].crudForm.items[0] as CrudFormMonacoCodeEditorItem).typeInRealTime = true;
    } else {
      (this.collapsePanelConfigs[CollapsePanels.ProjectDescription].crudForm.items[0] as CrudFormMonacoCodeEditorItem).typeInRealTime = false;
    }

    if (this.aiStatusProjectDescriptionImproved === AIProgressStatus.Completed) {
      // disable the improve with AI button if the project description is already improved
      if (this.collapsePanelConfigs[CollapsePanels.ProjectDescription]?.actionButtons?.length) {
        const improveProjectWithAIButton = this.collapsePanelConfigs[CollapsePanels.ProjectDescription].actionButtons.find(button =>
          button.key === ProjectDesignSettingButtons.ImproveProjectWithAI);
        if (improveProjectWithAIButton) {
          improveProjectWithAIButton.disabled = true;
          improveProjectWithAIButton.tooltipText = 'Project description is already improved';
        }
      }
    }
  }

  highlightActionButtons() {
    if (this.activeWizardStep) {
      this.removeHighlightEffectFromAllActionButtons();
      const activeStep = this.wizardStepsConfigs[this.activeWizardStep];
      const highlightedButtons = activeStep.highlightedElements;
      if (activeStep?.status !== WizardStepStatus.Completed && highlightedButtons?.length) {
        highlightedButtons.forEach(buttonKey => {
          if (buttonKey === ProjectDesignSettingButtons.ImproveProjectWithAI) {
            const projectDescriptionStep = this.wizardStepsConfigs?.find(wizardStep => wizardStep.key === WizardSteps.ProjectDescription);
            const projectDescriptionStatus = projectDescriptionStep?.status || WizardStepStatus.NotStarted;
            if (this.aiStatusProjectDescriptionImproved === AIProgressStatus.NotStarted && projectDescriptionStatus === WizardStepStatus.InProgress) {
              this.addHighlightEffectToActionButton(buttonKey);
            }
          } else {
            this.addHighlightEffectToActionButton(buttonKey);
          }
        });
      }
    }
  }

  // enableAndHighlightNextButton() {
  //   if (this.activeWizardStep) {
  //     // Highlight the next button to after the project description is improved
  //     this.addHighlightEffectToActionButton(ProjectDesignSettingButtons.WizardNextButton);
  //     this.wizardStepsConfigs[this.activeWizardStep].disableNextButton = false;
  //   }
  // }

  addHighlightEffectToActionButton(actionButtonKey: string) {
    setTimeout(() => {
      const actionButton = document.getElementById(actionButtonKey);
      if (actionButton) {
        this.renderer.addClass(actionButton, 'blinking-outline-shake');
      }
    }, 500);
  }

  removeHighlightEffectFromAllActionButtons() {
    const actionButtonKeys = Object.values(ProjectDesignSettingButtons);
    actionButtonKeys.forEach(key => {
      const actionButton = document.getElementById(key);
      if (actionButton) {
        this.renderer.removeClass(actionButton, 'blinking-outline-shake');
      }
    });
  }

  // returns true if the step is visited for the first time; meaning next step is not started
  isStepVisitedForTheFirstTime(key: WizardSteps): boolean {
    const stepIndex = this.wizardStepsConfigs.findIndex(step => step.key === key);
    const nextStepStatus = this.wizardStepsConfigs[stepIndex + 1].status;
    return nextStepStatus === WizardStepStatus.NotStarted;
  }

  // #endregion

  // #region Action / Setting Button Functions

  /* Action buttons on settings (right side) of the sections */
  onActionButtonClicked(actionKey: string) {
    console.log('Action button clicked:', actionKey);

    switch (actionKey) {
      case ProjectDesignSettingButtons.EditProjectDescription:
        this.projectDescriptionEditMode = !this.projectDescriptionEditMode;
        this.onCollapsePanelActiveChange(CollapsePanels.ProjectDescription, true);

        if (this.projectDescriptionEditMode) {
          const allStepsCompleted = this.activeWizardStep !== undefined &&
            (this.activeWizardStep >= 2);

          if (allStepsCompleted) {
            this.notificationService.createNotification(
              'info',
              'Project Description Edit Mode',
              'Changes to the project description will not affect the AI-generated code or functionality now, as AI has processed your description and built your code. Please use the AI tools for each feature to add and update your features if you want to modify your code.'
            );
          }
        }
        break;

      case ProjectDesignSettingButtons.ImproveProjectWithAI:
        // Update config to show the description typing in real time
        // this.setProjectDescriptionAsTypeInRealTime(true);
        this.improveProjectDescriptionWithAI();
        // this.removeHighlightEffectFromAllActionButtons();
        break;

      case ProjectDesignSettingButtons.AddAuthorization:
        this.notificationService.createNotification(
          'info',
          'Authentication Module Enabled',
          'Authentication module has already been added to the project. You can change the settings later'
        );
        break;

      case ProjectDesignSettingButtons.ChangeProgrammingLanguage:
        this.notificationService.createNotification(
          'info',
          'This feature is not available for free users',
          'We only provide NodeJS, Angular & MongoDB stack for free plans. Please upgrade to a premium plan to change the programming languages and frameworks',
        );
        break;

      case ProjectDesignSettingButtons.ChangeToMobileAppProject:
        this.notificationService.createNotification(
          'info',
          'This feature is not available for free users',
          'We only provide web application for free plans. Please upgrade to a premium plan to build mobile applications',
        );
        break;

      case ProjectDesignSettingButtons.InviteOthers:
        this.showInvitationModal = new Date().toISOString();
        break;

      case ProjectDesignSettingButtons.ProjectPackages: {
        this.navigateToUrl(FullPaths.ProjectPackages);
        break;
      }

      case ProjectDesignSettingButtons.EmbeddedExternalScripts: {
        this.navigateToUrl(FullPaths.EmbeddedScripts);
        break;
      }

      case ProjectDesignSettingButtons.ProjectMembers: {
        this.navigateToUrl(FullPaths.ProjectMembers);
        break;
      }
      case ProjectDesignSettingButtons.ProjectMembersList: {
        this.navigateToUrl(FullPaths.ProjectMembersList);
        break;
      }

      case ProjectDesignSettingButtons.ProjectRoles: {
        this.navigateToUrl(FullPaths.ProjectRoles);
        break;
      }

      case ProjectDesignSettingButtons.DisplayDataModelDiagram: {
        this.navigateToUrl(FullPaths.DatabaseDiagram);
        break;
      }


      case ProjectDesignSettingButtons.CreateDataModelManually: {
        this.navigateToUrl('/projects/model-create');
        break;
      }


      case ProjectDesignSettingButtons.DataModelList: {
        this.navigateToUrl(FullPaths.DataModels);
        break;
      }

      case ProjectDesignSettingButtons.DataSchemaList: {
        this.navigateToUrl(FullPaths.DataSchemas);
        break;
      }

      case ProjectDesignSettingButtons.DisplayApiEndpoints: {
        this.navigateToUrl(FullPaths.Endpoint);
        break;
      }

      case ProjectDesignSettingButtons.AddUiPageManually: {
        this.navigateToUrl('/pages/create');
        break;
      }

      case ProjectDesignSettingButtons.UiPageList: {
        this.navigateToUrl(FullPaths.Pages);
        break;
      }

      case ProjectDesignSettingButtons.UiModules: {
        this.navigateToUrl(FullPaths.Modules);
        break;
      }

      case ProjectDesignSettingButtons.UiPageLayoutList: {
        this.navigateToUrl(FullPaths.Layouts);
        break;
      }

      case ProjectDesignSettingButtons.ThemeList: {
        this.navigateToUrl(FullPaths.ThemeList);
        break;
      }

      case ProjectDesignSettingButtons.AddThemeManually: {
        this.navigateToUrl('/projects/create-theme');
        break;
      }

      case ProjectDesignSettingButtons.CustomStyles: {
        this.navigateToUrl(FullPaths.CSStyleCustomStyles);
        break;
      }

      case ProjectDesignSettingButtons.UiProcessDiagram: {
        this.navigateToUrl(FullPaths.DiagramEditor);
        break;
      }

      case ProjectDesignSettingButtons.UiDataStores: {
        this.navigateToUrl(FullPaths.UIDataStores);
        break;
      }

      case ProjectDesignSettingButtons.UiDataEvents: {
        this.navigateToUrl(FullPaths.Events);
        break;
      }

      case ProjectDesignSettingButtons.UiDataEventUpdateFunctions: {
        this.navigateToUrl(FullPaths.UIDataStoreUpdateFunctions);
        break;
      }

      case ProjectDesignSettingButtons.UiDataStepFunctions: {
        this.navigateToUrl(FullPaths.UIStepFunctions);
        break;
      }

      case ProjectDesignSettingButtons.MessageTemplateList: {
        this.navigateToUrl(FullPaths.MessageTemplates);
        break;
      }

      case ProjectDesignSettingButtons.AddMessageTemplateManually: {
        this.navigateToUrl('/projects/message-template');
        break;
      }

      case ProjectDesignSettingButtons.ServerSideBusinessProcessDiagram: {
        this.navigateToUrl(FullPaths.WorkflowDiagram);
        break;
      }

      case ProjectDesignSettingButtons.WorkflowCustomFunctionDefinitions: {
        this.navigateToUrl(FullPaths.WorkflowFunctions);
        break;
      }

      case ProjectDesignSettingButtons.DisplayProjectSettings: {
        this.navigateToUrl(FullPaths.ProjectSettings);
        break;
      }

      case ProjectDesignSettingButtons.DisplayEnvironmentVariables: {
        this.navigateToUrl(FullPaths.EnvironmentVariables);
        break;
      }

      case ProjectDesignSettingButtons.CreateDataModelWithAI: {
        this.navigateToAIGenerationPage(AIGenerationType.DATA_MODEL);
        break;
      }

      case ProjectDesignSettingButtons.AddEndpointWithAI: {
        this.navigateToAIGenerationPage(AIGenerationType.ENDPOINT);
        break;
      }

      case ProjectDesignSettingButtons.AddComponentWithAI: {
        this.navigateToAIGenerationPage(AIGenerationType.COMPONENT);
        break;
      }

      case ProjectDesignSettingButtons.AddThemeWithAI: {
        this.navigateToAIGenerationPage(AIGenerationType.THEME);
        break;
      }

      case ProjectDesignSettingButtons.AddPageLayoutWithAI: {
        this.navigateToAIGenerationPage(AIGenerationType.PAGE_LAYOUT);
        break;
      }

      case ProjectDesignSettingButtons.AddUiPageWithAI: {
        this.navigateToAIGenerationPage(AIGenerationType.PAGE);
        break;
      }

      case ProjectDesignSettingButtons.AddMessageTemplateWithAI: {
        this.navigateToAIGenerationPage(AIGenerationType.MESSAGE_TEMPLATE);
        break;
      }

      case ProjectDesignSettingButtons.AddWorkflowWithAI: {
        this.navigateToAIGenerationPage(AIGenerationType.WORKFLOW);
        break;
      }
    }
  }

  // #endregion

  // #region Display Code and Design Panels

  onDisplayCodePanelClicked() {
    this.displayCodePanelClicked.emit(Date.toString());
    this.collapseButtonClicked.emit((new Date()).toString());
  }

  // #endregion

  // #region Collapse Panel Visibility
  updateCollapsePanelVisibilities(mode: string, visiblePanelKeys?: string[]) {

    if (mode === 'expand-all') {
      this.allPanelsActive = true;
      this.visiblePanelKeys = [];
    } else if (mode === 'collapse-all') {
      this.allPanelsActive = false;
      this.visiblePanelKeys = [];
    } else {
      this.allPanelsActive = false;
      this.visiblePanelKeys = visiblePanelKeys || [];
    }
  }

  onCollapsePanelActiveChange(panelKey: CollapsePanels, active: boolean) {
    console.log('Collapse panel active change:', panelKey, active);
    // Check if this panel is visible
    const isPanelVisible = this.visiblePanelKeys.includes(panelKey);
    if (active && isPanelVisible) {
      return;
    }

    if (active) {
      this.visiblePanelKeys = [
        ...this.visiblePanelKeys.filter(key => !autoCollapsedPanels.includes(key as CollapsePanels)),
        panelKey
      ];
    } else {
      this.visiblePanelKeys = this.visiblePanelKeys.filter(key => key !== panelKey);
    }

    const wizardStepConfig = this.wizardStepsConfigs.find(step => step.collapsePanel === panelKey);

    // Code path updates
    if (wizardStepConfig?.layoutPaths?.codePath && this.activeCodePath !== wizardStepConfig.layoutPaths.codePath) {
      this.activeCodePath = wizardStepConfig.layoutPaths.codePath;
      this.getSourceCode(this.activeCodePath);
    }

    // Design path updates
    if (wizardStepConfig?.layoutPaths?.designPath) {
      this.activeDesignPath = wizardStepConfig.layoutPaths.designPath;
      console.log('*navigating from onCollapsePanelActiveChange to design path:', this.activeDesignPath);
      this.navigateToUrl(this.activeDesignPath);
    }

    // Layout mode updates
    if (wizardStepConfig?.layoutMode && this.layoutMode !== wizardStepConfig.layoutMode) {
      setTimeout(() => {
        this.layoutModeChanged.emit(wizardStepConfig.layoutMode);
      }, 10);
    }
  }

  isProjectDesignPlanningCompleted() {
    // Navigate to the current step path only if the planning is not completed
    const wizardStepsThatWillBeAutomaticallyProcessed = this.wizardStepsConfigs.filter(wizardStep => wizardStep.displayInPlanningTimeline);
    const completedWizardSteps = wizardStepsThatWillBeAutomaticallyProcessed.filter(wizardStep => wizardStep.status === WizardStepStatus.Completed);

    // check if the active step index is in range and the completed steps are less than the total steps
    if (completedWizardSteps?.length < wizardStepsThatWillBeAutomaticallyProcessed?.length) {
      return false;
    } else {
      return true;
    }
  }

  onCrudCollapseActiveChange(event: any) {
    if (event?.collapseSection?.key === CollapsePanels.ProjectInformation) {
      this.projectInformationCollapseActive = event.isActive;
    }
  }
  // #endregion

  // #region Collapse Panel and Content Animations

  // #region Animated Endpoints Source Code Scroll

  // #endregion

  scrollToElement(elementId: string, end = false) {
    const element = document.getElementById(elementId);
    if (element) {
      if (!end) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });
      }
    }
  }
  // #endregion

  // #region Post It Notes

  onPostItNoteUpdated(event: any) {
    console.log('Post it note updated:', event);
  }

  // TODO: Should return the post it notes from the data
  getPostItNotes(sectionKey: string): any[] {
    // TODO: Implement the logic to get the post it notes from the data
    return [
      // {
      //   id: '1',
      //   title: 'Post It Note 1',
      //   color: PostItColor.Yellow,
      //   size: PostItSize.Small,
      //   content: 'This is a post it note content',
      // }
    ];
  }

  // #endregion

  // // #region AI Functions


  improveProjectDescriptionWithAI() {
    const mockThisStep = false;

    if (this.activeProject?.description) {
      const params = {
        body: {
          message: this.activeProject?.description
        }
      };
      this.updateAiStatus('projectDescriptionImproved', AIProgressStatus.InProgress);

      let aiCall$: Observable<ImproveProjectDescriptionByAiResponseDto>;
      if (mockThisStep) {
        // create a mock rxjs observable to simulate the AI response
        aiCall$ = of({
          description: this.activeProject?.description || ''
        }).pipe(delay(1000));
      } else {
        aiCall$ = this.projectApi.generateByAI(params);
      }

      aiCall$.subscribe({
        next: (response) => {
          if (response.description) {
            this.updateAiStatus('projectDescriptionImproved', AIProgressStatus.Completed);
            this.projectDescriptionEditMode = false;
            if (this.activeProject?.id) {
              this.store.dispatch(new UpdateProject({
                project: {
                  description: response.description
                },
                projectId: this.activeProject.id,
                redirectToProjectDetail: false,
                notificationMessageOnSuccess: 'Project Description Improved with AI',
                notificationMessageOnFailure: 'Project Description Could Not Be Improved with AI'
              }));
            }
            this.wizardStepsConfigs[1].description = projectDescriptionStepCompletedContent;
          }
        },
        error: (error) => {
          this.updateAiStatus('projectDescriptionImproved', AIProgressStatus.Error);
          this.notificationService.createNotification(
            'error',
            error?.statusText || 'Error with AI',
            error?.error?.error?.message || 'Project Description Could Not Be Improved with AI',
          );
        }
      });
    }
  }

  // #endregion

  // #region Client Local State Functions

  updateAiStatus(key: string, value: AIProgressStatus) {
    this._saveClientLocalStateVariable(key, value.toString());
  }

  /**
   * ClientLocalState Saves to the: [IndexDB / projectId / _root / key: value]
   *
   * @param {string} key
   * @param {*} value
   * @memberof ProjectDesignComponent
   */
  private _saveClientLocalStateVariable(key: string, value: any) {
    if (this.activeProject?.id) {
      this.store.dispatch(CreateClientLocalState({ payload: { clientLocalState: <ClientLocalState>{ key, value } } }));
    }
  }

  // #endregion


  // #region Utility Functions
  // TODO: Move it to a service
  typeTextInRealTime(fullText: string, fieldReference: string): void {
    const typingSpeed = 100; // milliseconds
    let index = 0;

    const typeChar = () => {
      if (index < fullText.length) {
        fieldReference += fieldReference.charAt(index);
        index++;
        setTimeout(typeChar, typingSpeed);
      }
    };

    typeChar(); // initiate typing
  }

  // #endregion

  // #region SECTION SPECIFIC FUNCTIONS

  // #region Project Description
  onProjectDescriptionSaved(projectDescription: string) {
    console.log('Project description saved:', projectDescription);
    this.projectDescriptionEditMode = false;

    if (this.activeProject?.id) {
      this.store.dispatch(new UpdateProject({
        project: {
          description: projectDescription
        },
        projectId: this.activeProject.id,
        redirectToProjectDetail: false,
        notificationMessageOnSuccess: 'Project Description Updated',
        notificationMessageOnFailure: 'Project Description Could Not Be Updated',
      }));
    }
  }

  onProjectDescriptionEditCancelled() {
    console.log('Project description edit cancelled');
    this.projectDescriptionEditMode = false;
  }
  // #endregion

  // #region Data Model
  onDeleteDataFieldClick(response: ActionResponse, projectModelId: string) {
    const dataFieldToDelete = <ProjectModelField>response.data;

    this.store.dispatch(new DeleteProjectModelField(
      {
        deletedProjectModelField: dataFieldToDelete,
        projectModelId: projectModelId
      }
    ));
  }
  // #endregion


  // #endregion

  /**
  * this function clear css style of the content area and
  * re-set content area css style by page css style and layout css style if exists
  */

  // TODO: Implement the function
  hasManagerRole(): boolean {
    return true;
  }

  navigateEditProject() {
    this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_EDIT_PATH}/${this.activeProject?.id}` }));
  }

  navigateToProjectList() {
    this.store.dispatch(new Navigate({ url: PATH_DEFINITIONS.PROJECTS.PROJECT_LIST_PATH }));
  }

  navigateToUrl(url: string) {
    console.log('navigateToUrl', url);
    this.store.dispatch(new Navigate({ url }));
  }

  onMegaMenuClose() {
    this.megaMenuVisible = false;
  }


  onMegaMenuClicked(event: any) {
    if (event?.path) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    this.megaMenuVisible = false;
  }

  // TODO: Implement the function
  isAdminOrManagerUser() {
    // should return the data according to the role of the user
    return true;
  }

  onSubCollapsePanelActiveChange(subPanelId: string, isActive: boolean) {
    this.isSubCollapsePanelVisible[subPanelId] = !!isActive;
  }

  splitDragEnds(event: any) {
    console.log('splitDragEnds');

  }

  collapseBluePrintManager() {
    console.log('collapseBluePrintManager');
    this.collapseButtonClicked.emit((new Date()).toString());
  }

  // #region Project Source Code

  getSourceCode(sourceFilePath: string | undefined) {
    if (this.layoutMode !== LayoutMode.SourceCode) {
      this.layoutModeChanged.emit(LayoutMode.SourceCode);
    }
    if (sourceFilePath) {
      this.store.dispatch(GetProjectSourceCode({ payload: { sourceFilePath } }));
    }
  }


  getCustomCodes(sourceFilePath: string | undefined): void {
    if (sourceFilePath) {
      this.store.dispatch(GetProjectSourceFileCustomCodes({ payload: { sourceFilePath: sourceFilePath } }));
    }
  }

  getProjectSourceCodeFiles() {
    this.store.dispatch(GetProjectSourceFiles());
  }

  onDataModelRowClick(data: any) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    this.store.dispatch(FocusToRelatedModelId({ payload: { focusToRelatedModelId: data.id } }));
  }

  onDataModelRelationCardDetail(event: any) {
    const relation = event?.item?.data;
    const sourceModelId = relation?.sourceModelId;

    if (sourceModelId) {
      this.router.navigate(
        [`${PATH_DEFINITIONS.PROJECTS.PROJECT_MODEL_DATA_FIELD_LIST}/${sourceModelId}`],
        { queryParams: { tab: 'relations' } }
      );
    }
  }

  onEndpointRowActionClick(event: any) {
    const endpoint = event;

    if (endpoint?.path) {
      const controllerPath = this.getControllerPathFromEndpoint(endpoint.path) || '';
      if (controllerPath) {
        if (this.activeCodePath === controllerPath) {
          this.scrollToCode(endpoint.name);
        } else {
          this.activeCodePath = controllerPath;
          this.getSourceCode(this.activeCodePath);

          setTimeout(() => {
            const editor = (window as any).monaco?.editor?.getEditors()?.[0];
            if (editor) {
              setTimeout(() => {
                this.scrollToCode(endpoint.name);
              }, 4000);
            }
          }, 1000);
        }
      }
    }
  }

  getControllerPathFromEndpoint(endpointPath: string): string {
    const basePath = endpointPath.split('/')[0];

    const singularPath = basePath.endsWith('s')
      ? basePath.slice(0, -1)
      : basePath;

    return `backend/src/controllers/${singularPath}.controller.ts`;
  }

  scrollToCode(codeName: string): void {
    if (this.layoutMode !== LayoutMode.SourceCode) {
      this.layoutModeChanged.emit(LayoutMode.SourceCode);
    }
    const editor = (window as any).monaco?.editor?.getEditors()?.[0];

    if (editor) {
      const searchPatterns = [
        `async ${codeName}(`,
        `${codeName}(`,
        `${codeName}:`,
        `${codeName}=`
      ];

      const model = editor.getModel();
      const content = model.getValue();

      for (const pattern of searchPatterns) {
        const match = content.indexOf(pattern);
        if (match !== -1) {
          const targetLineNumber = content.substring(0, match).split('\n').length;
          const currentLineNumber = editor.getPosition().lineNumber;

          if (pattern.startsWith('async')) {
            const scrollDuration = 1500;
            const distance = targetLineNumber - currentLineNumber;
            const startTime = Date.now();

            const animate = () => {
              const currentTime = Date.now();
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / scrollDuration, 1);

              const easeInOutCubic = (p: number) => p < 0.5
                ? 4 * p * p * p
                : 1 - Math.pow(-2 * p + 2, 3) / 2;

              const currentLine = currentLineNumber + distance * easeInOutCubic(progress);
              editor.revealLineInCenter(Math.round(currentLine));

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                editor.setPosition({ lineNumber: targetLineNumber, column: 1 });
                editor.setSelection(new (window as any).monaco.Range(
                  targetLineNumber,
                  1,
                  targetLineNumber,
                  model.getLineMaxColumn(targetLineNumber)
                ));

                setTimeout(() => {
                  editor.setSelection(new (window as any).monaco.Range(
                    targetLineNumber,
                    1,
                    targetLineNumber,
                    1
                  ));
                }, 3000);
              }
            };

            animate();
          } else {
            editor.revealLineInCenter(targetLineNumber);
            editor.setPosition({ lineNumber: targetLineNumber, column: 1 });
            editor.setSelection(new (window as any).monaco.Range(
              targetLineNumber,
              1,
              targetLineNumber,
              model.getLineMaxColumn(targetLineNumber)
            ));

            setTimeout(() => {
              editor.setSelection(new (window as any).monaco.Range(
                targetLineNumber,
                1,
                targetLineNumber,
                1
              ));
            }, 3000);
          }
          break;
        }
      }
    }
  }

  onProjectSettingsRowActionClick(event: any) {
    const filePath = 'backend/src/definitions/settings.ts';
    const scrollToSettings = event?.key;

    if (this.activeCodePath === filePath) {
      if (scrollToSettings) {
        this.scrollToCode(scrollToSettings);
      }
    } else {
      this.activeCodePath = filePath;
      this.getSourceCodeAndRefreshCodeEditor(scrollToSettings);
    }
  }

  getSourceCodeAndRefreshCodeEditor(scrollToSettings: any) {
    this.getSourceCode(this.activeCodePath);
    setTimeout(() => {
      const editor = (window as any).monaco?.editor?.getEditors()?.[0];
      if (editor && scrollToSettings) {
        setTimeout(() => {
          this.scrollToCode(scrollToSettings);
        }, 1500);
      }
    }, 500);
  }

  onProjectEnvironmentVariablesRowActionClick(event: any) {
    const filePath = this.getEnvironmentVariablesPath(event.environmentKey);
    const scrollToEnvironmentVariable = event?.key;

    if (this.activeCodePath === filePath) {
      if (scrollToEnvironmentVariable) {
        this.scrollToCode(scrollToEnvironmentVariable);
      }
    } else {
      this.activeCodePath = filePath;
      this.getSourceCodeAndRefreshCodeEditor(scrollToEnvironmentVariable);
    }
  }

  getEnvironmentVariablesPath(environmentKey: string): string {
    const environmentFileName = environmentKey.toLowerCase();
    return `backend/environments/${environmentFileName}.env`;
  }

  // #endregion

  onElementComponentSelected(event: ComponentDefinitionWithRelations) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    if (event?.id) {
      this.scrollToElement(event.id);
    }
  }

  // #region DOM Functions

  scrollToDomElement(sectionId: string) {
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // #endregion

  // #region AI Chat Modal Methods - REMOVED
  // Modal functionality has been replaced with direct navigation to AI generation pages
  // #endregion


  // #region Action Card Click

  onCardsSelect(card: any) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    if (card.imageButtonData.cardOneButtonOutput.button.name === 'preview') {
      if (card.imageButtonData.item.data.type === 'page' || card.imageButtonData.item.data.type === 'layout') {
        this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.CONTENT_EDITOR.CONTENT_EDITOR_PATH}/${card.imageButtonData.item.data.id}` }));
      } else {
        this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_EDIT_MESSAGE_TEMPLATE}/${card.imageButtonData.item.data.id}` }));
      }
    }

    if (card.imageButtonData.cardOneButtonOutput.button.name === 'preview-page' || card.imageButtonData.cardOneButtonOutput.button.name === 'preview-layout') {
      this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.CONTENT_EDITOR.CONTENT_EDITOR_PATH}/${card.imageButtonData.item.data.id}` }));
    }


    if (card.imageButtonData.cardOneButtonOutput.button.name === 'preview-theme') {
      this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_THEME}/${card?.imageButtonData.item?.data?.id}` }));
    }

    if (card.imageButtonData.cardOneButtonOutput.button.name === 'view-code' && card.imageButtonData.item?.data?.id) {
      const filePath = `frontend/src/app/layouts/${card.imageButtonData.item.data.id}.component.ts`;
      this.activeCodePath = filePath;
      this.getSourceCode(this.activeCodePath);
      this.layoutModeChanged.emit(LayoutMode.SourceCode);
    }

    if (card.imageButtonData.cardOneButtonOutput.button.name === 'view-fields') {
      const modelId = card?.imageButtonData.item?.data?.id;
      if (modelId) {
        this.router.navigate([`${PATH_DEFINITIONS.PROJECTS.PROJECT_MODEL_DATA_FIELD_LIST}/${modelId}`], { queryParams: { tab: 'fields' } });
      }
    }

    if (card.imageButtonData.cardOneButtonOutput.button.name === 'view-data-store-detail') {
      const dataStoreId = card?.imageButtonData.item?.data?.id;
      if (dataStoreId) {
        this.router.navigate([`${PATH_DEFINITIONS.PROJECTS.UI_DATA_STORE_DETAIL}/${dataStoreId}`], { queryParams: QUERY_PARAM_DEFINITIONS.PROJECT.UI_DATA_STORE_DETAIL.FIELDS_TAB });
      }
    }
  }

  onCardDetail(cardDetail: any) {
    console.log('card detail test:', cardDetail);
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    if (cardDetail.item.type === 'page') {
      this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.CONTENT_EDITOR.CONTENT_EDITOR_PATH}/${cardDetail.item.data.id}` }));
    } else if (cardDetail.item.type === 'layout') {
      this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.CONTENT_EDITOR.CONTENT_EDITOR_PATH}/${cardDetail.item.data.id}` }));
      this.clickedLayoutCardId = cardDetail.item.data.id;
      this.store.dispatch(new ChangeDefaultLayout({ layoutId: cardDetail.item.data.id }));
    } else if (cardDetail.item.type === 'message-template') {
      this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_EDIT_MESSAGE_TEMPLATE}/${cardDetail.item.data.id}` }));
    } else if (cardDetail.item.type === 'theme') {
      const themeId = cardDetail.item?.data?.id;
      if (themeId) {
        this.clickedThemeCardId = themeId;
        if (this.activeProject?.id) {
          this.store.dispatch(new SetAsActiveProjectTheme({
            projectThemeId: themeId,
            activeProjectId: this.activeProject.id
          }));
        }
        this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_THEME}/${themeId}` }));
      }
    } else if (cardDetail.item.type === 'project-version') {
      const versionId = cardDetail.item?.data?.id;
      if (versionId) {
        this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_VERSION_DETAIL}/${versionId}` }));
      }
    } else if (cardDetail.item.type === 'data-model') {
      const modelId = cardDetail.item?.data?.id;
      if (modelId) {
        const currentUrl = this.router.url;
        const isOnDbDiagramPage = currentUrl.includes('/db-diagram-editor');

        if (!isOnDbDiagramPage) {
          this.store.dispatch(new Navigate({
            url: `${PATH_DEFINITIONS.DATABASE_DIAGRAM_EDITOR.DATABASE_DIAGRAM_EDITOR_PATH}`
          }));
          setTimeout(() => {
            this.store.dispatch(FocusToRelatedModelId({ payload: { focusToRelatedModelId: modelId } }));
          }, 100);
        } else {
          this.store.dispatch(FocusToRelatedModelId({ payload: { focusToRelatedModelId: modelId } }));
        }
      }
    } else if (cardDetail.item.type === 'ui-data-store') {
      const dataStoreId = cardDetail.item?.data?.id;
      if (dataStoreId) {
        const currentUrl = this.router.url;
        const isOnWorkflowDiagramPage = currentUrl.includes('/ui-workflow-diagram');

        if (!isOnWorkflowDiagramPage) {
          this.store.dispatch(new Navigate({
            url: '/ui-workflow-diagram'
          }));
          setTimeout(() => {
            this.store.dispatch(FocusToRelatedDataStoreId({ payload: { focusToRelatedDataStoreId: dataStoreId } }));
          }, 100);
        } else {
          this.store.dispatch(FocusToRelatedDataStoreId({ payload: { focusToRelatedDataStoreId: dataStoreId } }));
        }
      }
    } else if (cardDetail.item.type === 'project-members') {
      const projectMembersCardId = cardDetail.item.data?.id;
      if (projectMembersCardId) {
        this.selectTabService.setActiveTab(projectMembersCardId);
      }
    } else if (cardDetail.item.type === 'share-application') {
      const shareApplicationCardId = cardDetail.item.data?.id;
      if (shareApplicationCardId) {
        this.buttonTriggerService.triggerButton(shareApplicationCardId);
      }
    } else if (cardDetail.item.type === 'server-toggle-power') {
      const currentUrl = this.router.url;
      if (!currentUrl.includes('/server-page')) {
        this.store.dispatch(new Navigate({ url: '/server-page' }));
      }
    } else if (cardDetail.item.type === 'serverside-business-processes') {
      this.router.navigate(['/workflow-diagram'], { queryParams: { filteredWorkflowId: cardDetail?.item?.data?.id } });
    }
  }

  onThemetopCardClick(event: CardOneListCardClickOutput) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    const cardType = event.item.data.type;
    if (cardType === 'create-blank-theme') {
      this.createBlankTheme();
    } else if (cardType === AIGenerationType.THEME) {
      this.createThemeWithAI();
    }
  }

  createBlankTheme() {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_THEME_CREATE}` }));
  }

  createThemeWithAI() {
    this.navigateToAIGenerationPage(AIGenerationType.THEME);
  }

  onLayouttopCardClick(event: CardOneListCardClickOutput) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    const cardType = event.item.data.type;
    if (cardType === 'create-blank-layout') {
      this.createBlankLayout();
    } else if (cardType === AIGenerationType.PAGE_LAYOUT) {
      this.createLayoutWithAI();
    }
  }

  createBlankLayout() {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PAGES.PAGE_LAYOUT_CREATE_PATH}` }));
  }

  createLayoutWithAI() {
    this.navigateToAIGenerationPage(AIGenerationType.PAGE_LAYOUT);
  }

  onVersiontopCardClick(event: CardOneListCardClickOutput) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    const cardType = event.item.data.type;
    if (cardType === 'create-blank-version') {
      this.createBlankVersion();
    }
  }

  createBlankVersion() {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_VERSION_CREATE}` }));
  }

  onDataModeltopCardClick(event: CardOneListCardClickOutput) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    const cardType = event.item.data.type;
    if (cardType === 'create-blank-data-model') {
      this.createBlankDataModel();
    } else if (cardType === AIGenerationType.DATA_MODEL) {
      this.navigateToAIGenerationPage(AIGenerationType.DATA_MODEL);
    }
  }

  createBlankDataModel() {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_MODEL_CREATE}` }));
  }

  createDataModelWithAI() {
    this.navigateToAIGenerationPage(AIGenerationType.DATA_MODEL);
  }

  onUIDataStoretopCardClick(event: CardOneListCardClickOutput) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    const cardType = event.item.data.type;
    if (cardType === 'create-blank-data-store') {
      this.createBlankDataStore();
    } else if (cardType === AIGenerationType.DATA_STORE) {
      this.createDataStoreWithAI();
    }
  }

  createBlankDataStore() {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.UI_DATA_STORE_CREATE}` }));
  }

  createDataStoreWithAI() {
    this.navigateToAIGenerationPage(AIGenerationType.DATA_STORE);
  }

  /**
   * Navigate to specific AI generation page based on type
   */
  navigateToAIGenerationPage(type: AIGenerationType) {
    const config = AI_CHAT_CONFIGS[type];
    if (config?.redirectUrl) {
      this.store.dispatch(new Navigate({ url: config.redirectUrl }));
    } else {
      console.warn(`No redirect URL configured for AI generation type: ${type}`);
    }
  }

  onPagetopCardClick(event: CardOneListCardClickOutput) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    const cardType = event.item.data.type;
    if (cardType === 'create-blank-page') {
      this.createBlankPage();
    } else if (cardType === AIGenerationType.PAGE) {
      this.createPageWithAI();
    }
  }

  createBlankPage() {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    console.log('Create Blank Page');
    this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PAGES.PAGE_CREATE_PATH}` }));
  }

  createPageWithAI() {
    this.navigateToAIGenerationPage(AIGenerationType.PAGE);
  }

  onTemplatetopCardClick(event: CardOneListCardClickOutput) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    const cardType = event.item.data.type;
    if (cardType === 'create-blank-template') {
      this.createBlankTemplate();
    } else if (cardType === AIGenerationType.MESSAGE_TEMPLATE) {
      this.navigateToAIGenerationPage(AIGenerationType.MESSAGE_TEMPLATE);
    }
  }

  createBlankTemplate() {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    console.log('Create Blank Template');
    this.store.dispatch(new Navigate({ url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_MESSAGE_TEMPLATE}` }));
  }

  createTemplateWithAI() {
    this.navigateToAIGenerationPage(AIGenerationType.MESSAGE_TEMPLATE);
  }

  onEndpointtopCardClick(event: CardOneListCardClickOutput): void {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }
    const cardType = event.item.data.type;
    if (cardType === AIGenerationType.ENDPOINT) {
      this.createCustomEndpointWithAI();
    }
  }

  createCustomEndpointWithAI() {
    this.navigateToAIGenerationPage(AIGenerationType.ENDPOINT);
  }

  clickEndpointCard(event: any): void {
    console.log('Endpoint card event:', event);

    if (event?.imageButtonData) {
      const buttonName = event.imageButtonData?.cardOneButtonOutput?.button?.name;
      const modelData = event.imageButtonData?.item?.data;

      if (buttonName === 'preview-code' && modelData?.name) {
        const controllerName = this.convertToKebabCase(modelData.name);
        const filePath = `backend/src/controllers/${controllerName}.controller.ts`;
        this.activeCodePath = filePath;
        this.getSourceCode(this.activeCodePath);
        this.layoutModeChanged.emit(LayoutMode.SourceCode);
      }
    }
    if (event?.item?.data?.name) {
      if (this.layoutMode !== LayoutMode.ProjectDesign) {
        this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
      }
      const modelId = event.item.data.id;
      this.scrollTriggerService.triggerScrollToModel(modelId);
    }
  }

  private convertToKebabCase(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase()
      .replace(/\s+controller$/, '');
  }

  onProjectSettingsCardDetail(event: any) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }

    const cardType = event.item.data.type;

    if (cardType === 'project-setting' || cardType === 'custom-settings') {
      const settingsTabKey = cardType === 'project-setting' ? 'system' : 'custom';
      this.navigateToUrl(FullPaths.ProjectSettings);
      this.selectTabService.setActiveTab(settingsTabKey);
    }
  }

  onProjectSettingsCardAction(event: any) {
    if (event?.imageButtonData) {
      const buttonName = event.imageButtonData?.cardOneButtonOutput?.button?.key;
      const cardType = event.imageButtonData?.item?.data?.type;
      const cardData = event.imageButtonData?.item?.data;

      if (buttonName === 'preview-code') {
        if (cardType === 'project-setting') {
          this.onProjectSettingsRowActionClick(cardData);
          this.layoutModeChanged.emit(LayoutMode.SourceCode);
        }
      }
    }
  }

  onProjectEnvironmentCardDetail(event: any) {
    if (this.layoutMode !== LayoutMode.ProjectDesign) {
      this.layoutModeChanged.emit(LayoutMode.ProjectDesign);
    }

    if (event.item.type === 'environment') {
      this.navigateToUrl(FullPaths.EnvironmentVariables);
      this.selectTabService.setActiveTab(event.item.data?.environmentKey);
    }
  }

  onProjectEnvironmentCardAction(event: any) {
    if (event?.imageButtonData) {
      const buttonName = event.imageButtonData?.cardOneButtonOutput?.button?.name;
      const cardData = event.imageButtonData?.item?.data;
      if (buttonName === 'preview-code') {
        this.onProjectEnvironmentVariablesRowActionClick(cardData);
        this.layoutModeChanged.emit(LayoutMode.SourceCode);
      }
    }
  }

  onProjectMemberstopCardClick(event: CardOneListCardClickOutput) {
    const projectMembersCardId = event.item.data?.id;
    if (projectMembersCardId === 'invite-user') {
      this.buttonTriggerService.triggerButton('openInviteUserModal');
    }
  }

  onShareApplicationtopCardClick(event: CardOneListCardClickOutput) {
    const shareApplicationCardType = event.item.data.type;
    if (shareApplicationCardType === 'explore-rapider-studio') {
      this.buttonTriggerService.triggerButton(shareApplicationCardType);
    }
  }

  onDeploytopCardClick(event: CardOneListCardClickOutput) {
    const deployCardType = event.item.data.type;
    if (deployCardType === 'deploy-project') {
      const isDeploymentInProgress = this.deployStatus === DeployStatus.InProgress || this.deployStatus === DeployStatus.Queued;
      if (!isDeploymentInProgress) {
        this.deployButtonClick();
      }
    }
  }

  // #endregion

  // #region map items

  updateServerCardStatus(deployManagement: any) {
    if (this.serverCardsConfig?.cardsConfig?.items) {
      const serverCard = this.serverCardsConfig.cardsConfig.items.find(item => item.type === 'server-toggle-power');
      if (serverCard && serverCard.descriptions && serverCard.descriptions.length > 0) {
        let statusText = 'Server Status: Project Not Deployed';
        let tooltipText = 'Please deploy your application first before managing server power state.';
        let customIcon = null;
        let disabledCard = true;

        const applicationState = deployManagement?.applicationState;
        if (applicationState === 'exited') {
          statusText = 'Server Status: Stopped';
          customIcon = {
            name: 'fas fa-plug-circle-xmark',
            type: IconType.FontAwesome,
            color: '#ff4d4f',
            size: '1.4rem'
          };
          disabledCard = false;
        } else if (applicationState === 'running') {
          statusText = 'Server Status: Running';
          customIcon = {
            name: 'fas fa-plug-circle-check',
            type: IconType.FontAwesome,
            color: '#52c41a',
            size: '1.4rem'
          };
          disabledCard = false;
        } else if (applicationState === 'starting') {
          statusText = 'Server Status: Starting...';
          customIcon = {
            name: 'fas fa-plug-circle-bolt',
            type: IconType.FontAwesome,
            color: '#1890ff',
            size: '1.4rem'
          };
          disabledCard = false;
        }
        serverCard.descriptions[0].content = statusText;
        serverCard.cardTooltipContent = tooltipText;
        serverCard.customIcon = customIcon as any;
        serverCard.disabledCard = disabledCard;
      }
    }
  }

  mapEndpointItems() {
    if (this.projectModels?.length && this.endpointsCardsConfig?.cardsConfig) {
      const endpointCards = this.projectModels.map(model => ({
        data: model,
        type: 'endpoint',
        titles: [
          {
            type: HeadingType.H4,
            content: `${model.name || 'Unnamed'} Controller`
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: `API endpoints for ${model.name || 'Unnamed Model'}`
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: model?.thumbnails?.length > 0 ?
            model.thumbnails[0].url : 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageTags: [{}],
        imageButtons: [
          {
            name: 'preview-code',
            icon: { name: 'far fa-code', size: 'var(--card-icon-size)' },
            customColorSettings: {
              backgroundColor: 'var(--section-background-color)',
              color: 'var(--text-color)'
            },
            shadowSettings: {
              boxShadow: '0 0 0 var(--section-background-color)'
            },
            borderSettings: {
              border: 'var(--border-size-x)',
              borderColor: 'var(--secondary-border-color)',
              borderRadius: 'var(--border-radius-5x)'
            },
            size: ButtonSize.Default,
            tooltipText: 'Preview Code'
          }
        ]
      }));

      this.endpointsCardsConfig.cardsConfig.items = endpointCards;
    }
  }

  mapProjectEnvironmentItems() {
    if (this.environments?.length && this.projectEnvironmentCardsConfig?.cardsConfig) {
      const environmentCards = this.environments.map(environmentKey => ({
        data: {
          id: environmentKey,
          environmentKey,
        },
        type: 'environment',
        titles: [
          {
            type: HeadingType.H4,
            content: `${environmentKey.charAt(0).toUpperCase() + environmentKey.slice(1)} Environment`
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: `Environment variables and configuration for ${environmentKey} environment`
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageTags: [{}],
        imageButtons: [
          {
            name: 'preview-code',
            icon: { name: 'far fa-code', size: 'var(--card-icon-size)' },
            customColorSettings: {
              backgroundColor: 'var(--section-background-color)',
              color: 'var(--text-color)'
            },
            shadowSettings: {
              boxShadow: '0 0 0 var(--section-background-color)'
            },
            borderSettings: {
              border: 'var(--border-size-x)',
              borderColor: 'var(--secondary-border-color)',
              borderRadius: 'var(--border-radius-5x)'
            },
            size: ButtonSize.Default,
            tooltipText: 'Preview Code'
          }
        ]
      }));

      this.projectEnvironmentCardsConfig.cardsConfig.items = environmentCards;
    } else {
      this.projectEnvironmentCardsConfig.cardsConfig.items = [];
    }
  }

  mapPageItems() {
    this.grouppedCards = this.modules?.map(module => ({
      key: module.title,
      value: module.id
    })).sort((a, b) => {
      if (a.key === 'Main') {
        return -1;
      }
      if (b.key === 'Main') {
        return 1;
      }
      return a.key.localeCompare(b.key);
    });

    if (this.pages?.map?.length) {
      this.pageCardsConfig.cardsConfig.items = this.pages?.map(template => ({
        data: {
          ...template,
          moduleId: template.moduleId
        },
        type: 'page',
        titles: [
          {
            type: HeadingType.H4,
            content: template.title
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: template.description
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: template?.thumbnails?.length > 0 ? template.thumbnails[0].url : 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageTags: [{}],
        imageButtons: [{
          name: 'preview-page',
          icon: {
            name: 'eye',
            type: IconType.NgZorro,
            theme: NgZorroIconTheme.Outline,
            size: 'var(--card-icon-size)'
          },
          customColorSettings: {
            backgroundColor: 'var(--section-background-color)',
            color: 'var(--text-color)'
          },
          shadowSettings: {
            boxShadow: '0 0 0 var(--section-background-color)'
          },
          borderSettings: {
            border: 'var(--border-size-x)',
            borderColor: 'var(--secondary-border-color)',
            borderRadius: 'var(--border-radius-5x)'
          },
          size: ButtonSize.Default,
          tooltipText: 'Preview'
        }]
      })) as any;
    }
  }

  mapLayoutItems() {
    if (this.layouts?.length) {
      this.layoutCardsConfig.cardsConfig.items = this.layouts?.map(template => ({
        data: template,
        type: 'layout',
        titles: [
          {
            type: HeadingType.H4,
            content: template.title
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: template.description
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: template?.thumbnails?.length > 0 ?
            template.thumbnails[0].url : 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageTags: [{}],
        cardTooltipContent: template.id === this.activeLayout?.id ? 'Active Layout - This is currently the default layout for your project' : undefined,
        imageButtons: [{
          name: 'preview-layout',
          icon: {
            name: 'eye',
            type: IconType.NgZorro,
            theme: NgZorroIconTheme.Outline,
            size: 'var(--card-icon-size)'
          },
          customColorSettings: {
            backgroundColor: 'var(--section-background-color)',
            color: 'var(--text-color)'
          },
          shadowSettings: {
            boxShadow: '0 0 0 var(--section-background-color)'
          },
          borderSettings: {
            border: 'var(--border-size-x)',
            borderColor: 'var(--secondary-border-color)',
            borderRadius: 'var(--border-radius-5x)'
          },
          size: ButtonSize.Default,
          tooltipText: 'Preview'
        }],
      })) as any;
    }
  }

  mapThemeItems() {
    if (this.projectThemes?.length) {
      this.themeCardsConfig.cardsConfig.items = this.projectThemes?.map(template => ({
        data: template,
        type: 'theme',
        titles: [
          {
            type: HeadingType.H4,
            content: template.name
          }
        ],
        image: {
          source: template?.thumbnails?.length > 0 ? template.thumbnails[0].url : 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageTags: [{}],
        cardTooltipContent: template.id === this.projectTheme?.id ? 'Active Theme - This is currently the active theme for your project' : undefined,
        imageButtons: [{
          name: 'preview-theme',
          icon: {
            name: 'eye',
            type: IconType.NgZorro,
            theme: NgZorroIconTheme.Outline,
            size: 'var(--card-icon-size)'
          },
          customColorSettings: {
            backgroundColor: 'var(--section-background-color)',
            color: 'var(--text-color)'
          },
          shadowSettings: {
            boxShadow: '0 0 0 var(--section-background-color)'
          },
          borderSettings: {
            border: 'var(--border-size-x)',
            borderColor: 'var(--secondary-border-color)',
            borderRadius: 'var(--border-radius-5x)'
          },
          size: ButtonSize.Default,
          tooltipText: 'Preview'
        }],
      }));
    }
  }

  mapProjectVersionItems() {
    if (this.projectVersions?.length && this.projectVersionCardsConfig?.cardsConfig) {
      this.projectVersionCardsConfig.cardsConfig.items = this.projectVersions.map(version => ({
        data: version,
        type: 'project-version',
        titles: [
          {
            type: HeadingType.H4,
            content: `Version ${version.versionNumber}`
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: `Created by ${version.createdBy} on ${new Date(version.createdDate).toLocaleDateString('en-US')}`
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageTags: [{}],
      }));
    } else {
      this.projectVersionCardsConfig.cardsConfig.items = [];
    }
  }

  mapDeployProjectItems() {
    if (this.deployCardsConfig?.cardsConfig) {
      const isDeploymentInProgress = this.deployStatus === DeployStatus.InProgress || this.deployStatus === DeployStatus.Queued;
      const deployProjectCard = this.deployCardsConfig.cardsConfig.topCards?.find(card => card.type === 'deploy-project');

      if (deployProjectCard) {
        if (isDeploymentInProgress) {
          deployProjectCard.disabledCard = true;
          deployProjectCard.cardTooltipContent = 'Deployment is already in progress. Please wait for the current deployment to complete.';
        } else {
          deployProjectCard.disabledCard = false;
          deployProjectCard.cardTooltipContent = undefined;
        }
      }

      if (!this.deployInfos || this.deployInfos.length === 0) {
        this.deployCardsConfig.cardsConfig.items = [{
          data: null,
          type: 'deploy',
          titles: [
            { type: HeadingType.H4, content: 'No Deployment Started' }
          ],
          descriptions: [
            {
              typography: { fontSize: 'var(--subtext-font-size)' },
              content: 'Your project has not been deployed yet. Start the deployment process to see deployment information here.'
            }
          ],
          isTooltipEnabledOnDescription: true,
          image: { source: 'assets/img/page-templates/profile-template1-thumbnail.png' },
          imageTags: [{}]
        }];
        return;
      }

      const latestDeploys = this.deployInfos.slice(0, 4);
      const items = latestDeploys.map((deploy: any) => {
        const created = deploy?.createdDate ? new Date(deploy.createdDate) : undefined;
        const createdText = created ? created.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : 'Unknown date';

        const status = (deploy?.status || '').toString();
        const statusUpper = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
        const containerInfo = deploy?.Names ? `Container: ${deploy.Names}` : undefined;
        const runningTime = deploy?.RunningFor ? `Running: ${deploy.RunningFor}` : undefined;

        let customIcon = null;
        if (status === DeployStatus.Successful) {
          customIcon = {
            name: 'fas fa-check-circle',
            type: IconType.FontAwesome,
            color: '#52c41a',
            size: '2.4rem'
          };
        } else if (status === DeployStatus.InProgress) {
          customIcon = {
            name: 'fas fa-spinner fa-spin',
            type: IconType.FontAwesome,
            color: '#1890ff',
            size: '2.4rem'
          };
        } else if (status === DeployStatus.Failure) {
          customIcon = {
            name: 'fas fa-times-circle',
            type: IconType.FontAwesome,
            color: '#ff4d4f',
            size: '2.4rem'
          };
        } else if (status === DeployStatus.Queued) {
          customIcon = {
            name: 'fas fa-clock',
            type: IconType.FontAwesome,
            color: '#faad14',
            size: '2.4rem'
          };
        } else if (status === DeployStatus.Canceled) {
          customIcon = {
            name: 'fas fa-ban',
            type: IconType.FontAwesome,
            color: '#8c8c8c',
            size: '2.4rem'
          };
        }


        let versionText;
        if (deploy?.projectVersionId && this.projectVersions?.length) {
          const projectVersion = this.projectVersions.find(pv => pv.id === deploy.projectVersionId);
          if (projectVersion?.versionNumber) {
            versionText = `Version: ${projectVersion.versionNumber}`;
          }
        }

        return <CardOneComponentConfig>{
          data: deploy,
          type: 'deploy',
          titles: [
            { type: HeadingType.H4, content: `Deployment - ${createdText}` }
          ],
          descriptions: [
            { typography: { fontSize: 'var(--subtext-font-size)' }, content: `Status: ${statusUpper}` },
            ...(versionText ? [{ typography: { fontSize: 'var(--subtext-font-size)' }, content: versionText }] : []),
            ...(containerInfo ? [{ typography: { fontSize: 'var(--subtext-font-size)' }, content: containerInfo }] : []),
            ...(runningTime ? [{ typography: { fontSize: 'var(--subtext-font-size)' }, content: runningTime }] : [])
          ],
          isTooltipEnabledOnDescription: true,
          image: { source: 'assets/img/page-templates/profile-template1-thumbnail.png' },
          imageTags: [{}],
          customIcon: customIcon
        };
      });
      this.deployCardsConfig.cardsConfig.items = items;
    }
  }

  mapDataModelItems() {
    if (this.projectModels?.length && this.dataModelCardsConfig?.cardsConfig) {
      this.dataModelCardsConfig.cardsConfig.items = this.projectModels.map(model => ({
        data: model,
        type: 'data-model',
        titles: [
          {
            type: HeadingType.H4,
            content: model.name
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: `${model.fields?.length || 0} fields • ${model.relations?.length || 0} relations`
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageTags: [{}],
        imageButtons: [
          {
            name: 'view-fields',
            icon: {
              name: 'unordered-list',
              type: IconType.NgZorro,
              theme: NgZorroIconTheme.Outline,
              size: 'var(--card-icon-size)'
            },
            customColorSettings: {
              backgroundColor: 'var(--section-background-color)',
              color: 'var(--text-color)'
            },
            shadowSettings: {
              boxShadow: '0 0 0 var(--section-background-color)'
            },
            borderSettings: {
              border: 'var(--border-size-x)',
              borderColor: 'var(--secondary-border-color)',
              borderRadius: 'var(--border-radius-5x)'
            },
            size: ButtonSize.Default,
            tooltipText: 'View Fields'
          }
        ]
      }));
    } else {
      this.dataModelCardsConfig.cardsConfig.items = [];
    }
  }

  mapDataModelRelationItems() {
    if (this.projectModelRelations?.length && this.dataModelRelationsCardsConfig?.cardsConfig) {
      this.dataModelRelationsCardsConfig.cardsConfig.items = this.projectModelRelations.map(relation => {
        const sourceModelName = relation.sourceModel?.name || 'Unknown';
        const targetModelName = relation.targetModel?.name || 'Unknown';
        const throughModelName = relation.throughModel?.name;
        const relationType = relation.type || 'Unknown';
        const descriptions = [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: `Source Model: ${sourceModelName}`
          },
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: `Relation Type: ${relationType}`
          },
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: `Target Model: ${targetModelName}`
          }
        ];

        if (throughModelName) {
          descriptions.splice(2, 0, {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: `Through: ${throughModelName}`
          });
        }

        return {
          data: relation,
          type: 'data-model-relation',
          titles: [
            {
              type: HeadingType.H4,
              content: relation.name || 'Unnamed Relation'
            }
          ],
          descriptions: descriptions,
          isTooltipEnabledOnDescription: true,
          image: {
            source: 'assets/img/page-templates/profile-template1-thumbnail.png'
          },
          imageTags: [{}],
        };
      });
    } else {
      this.dataModelRelationsCardsConfig.cardsConfig.items = [];
    }
  }

  mapMessageTemplateItems() {
    if (this.messageTemplates?.length && this.messageTemplatesCardsConfig?.cardsConfig) {
      this.messageTemplatesCardsConfig.cardsConfig.items = this.messageTemplates.map(template => ({
        data: template,
        type: 'message-template',
        titles: [
          {
            type: HeadingType.H4,
            content: template?.key ?? 'No Key'
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: template.description
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: (template?.thumbnails?.[0]?.url) || 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageTags: [{}]
      }));
    } else {
      this.messageTemplatesCardsConfig.cardsConfig.items = []; // Clear items if no templates
    }
  }

  mapUIDataStoreItems() {
    if (this.uiDataStores?.length && this.uiDataStoreCardsConfig?.cardsConfig) {
      this.uiDataStoreCardsConfig.cardsConfig.items = this.uiDataStores.map(dataStore => ({
        data: dataStore,
        type: 'ui-data-store',
        titles: [
          {
            type: HeadingType.H4,
            content: dataStore?.name ?? 'No Name'
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: dataStore?.description || 'No description available'
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageButtons: [
          {
            name: 'view-data-store-detail',
            icon: {
              name: 'eye',
              type: IconType.NgZorro,
              theme: NgZorroIconTheme.Outline,
              size: 'var(--card-icon-size)'
            },
            customColorSettings: {
              backgroundColor: 'var(--section-background-color)',
              color: 'var(--text-color)'
            },
            shadowSettings: {
              boxShadow: '0 0 0 var(--section-background-color)'
            },
            borderSettings: {
              border: 'var(--border-size-x)',
              borderColor: 'var(--secondary-border-color)',
              borderRadius: 'var(--border-radius-5x)'
            },
            size: ButtonSize.Default,
            tooltipText: 'View Data Store Detail'
          }
        ],
        imageTags: [{}]
      }));
    } else {
      this.uiDataStoreCardsConfig.cardsConfig.items = []; // Clear items if no data stores
    }
  }

  mapServerSideBusinessProcessesItems() {
    if (this.starterNodes?.length && this.serverSideBusinessProcessesCardsConfig?.cardsConfig) {
      this.serverSideBusinessProcessesCardsConfig.cardsConfig.items = this.starterNodes.map(workflow => ({
        data: workflow,
        type: 'serverside-business-processes',
        titles: [
          {
            type: HeadingType.H4,
            content: workflow?.name ?? 'No Name'
          }
        ],
        descriptions: [
          {
            typography: {
              fontSize: 'var(--subtext-font-size)'
            },
            content: workflow?.description || 'No description available'
          }
        ],
        isTooltipEnabledOnDescription: true,
        image: {
          source: 'assets/img/page-templates/profile-template1-thumbnail.png'
        },
        imageButtons: [
          {
            name: 'view-data-store-detail',
            icon: {
              name: 'eye',
              type: IconType.NgZorro,
              theme: NgZorroIconTheme.Outline,
              size: 'var(--card-icon-size)'
            },
            customColorSettings: {
              backgroundColor: 'var(--section-background-color)',
              color: 'var(--text-color)'
            },
            shadowSettings: {
              boxShadow: '0 0 0 var(--section-background-color)'
            },
            borderSettings: {
              border: 'var(--border-size-x)',
              borderColor: 'var(--secondary-border-color)',
              borderRadius: 'var(--border-radius-5x)'
            },
            size: ButtonSize.Default,
            tooltipText: 'View Data Store Detail'
          }
        ],
        imageTags: [{}]
      }));
    } else {
      this.uiDataStoreCardsConfig.cardsConfig.items = [];
    }
  }

  subscribeToDiagramData() {
    const sources = {
      events: this.store.select(state => state.workflowEvent.data),
      steps: this.store.select(state => state.workflowStepFunction.data),
      subscribed: this.store.select(state => state.workflowStepFunctionSubscribedEvent.data),
      publishedSuccess: this.store.select(state => state.workflowStepFunctionPublishedEventOnSuccess.data),
      publishedFailure: this.store.select(state => state.workflowStepFunctionPublishedEventOnFailure.data),
      isFetched: this.store.select(isEveryDataFetched)
    };

    return combineLatest(sources).pipe(
      filter(data => data.isFetched)
    ).subscribe(data => {
      this.workflowEvents = data.events || [];
      this.workflowStepFunctions = data.steps || [];
      this.subscribedEvents = data.subscribed || [];
      this.publishedEventsOnSuccess = data.publishedSuccess || [];
      this.publishedEventsOnFailure = data.publishedFailure || [];
      this.updateStarterNodes();
    });
  }

  updateStarterNodes() {
    if (!this.workflowEvents || !this.workflowStepFunctions || !this.publishedEventsOnSuccess || !this.publishedEventsOnFailure || !this.subscribedEvents) {
      this.starterNodes = [];
      return;
    }

    const allPublishedEventIds = new Set([
      ...this.publishedEventsOnSuccess.map(e => e.workflowEventId),
      ...this.publishedEventsOnFailure.map(e => e.workflowEventId)
    ]);
    console.log(this.workflowEvents)
    console.log(this.workflowStepFunctions)
    const starterEvents = this.workflowEvents
      .filter(event => !allPublishedEventIds.has(event.id))
      .map(event => ({ id: event.id, name: `${event.name} (Event)`, description: event?.description }));

    const allTargetedStepFunctionIds = new Set(
      this.subscribedEvents.map(e => e.workflowStepFunctionId)
    );

    const starterStepFunctions = this.workflowStepFunctions
      .filter(step => !allTargetedStepFunctionIds.has(step.id))
      .map(step => ({ id: step.id, name: `${step.name} (Step Function)`, description: step?.description }));

    this.starterNodes = [...starterEvents, ...starterStepFunctions].sort((a, b) => a.name.localeCompare(b.name));
    this.mapServerSideBusinessProcessesItems();
  }

  // #endregion

  onWizardModalTypingsCompleted(event: any) {
    console.log('*** AppComponent onWizardModalTypingsCompleted', event);
    // static 3 number means scopeOfWork step. Wizards should wait for data models completed
    if (event.typingsCompleted) {
      if (event.wizardStep === this.activeWizardStep && event.projectId === this.activeProject?.id && event.wizardStep !== 3) {
        this.triggerNextWizardStep();
      }
      if (event.wizardStep === this.activeWizardStep && event.projectId === this.activeProject?.id && event.wizardStep === 3 && this.projectModelCreationIsCompleted) {
        this.triggerNextWizardStep();
      }
    }
  }

  /**
   * Handle theme change in real-time
   * @param themeId - The new active theme ID
   */
  private handleThemeChange(themeId: string) {
    this.clickedThemeCardId = themeId;
    if (this.projectThemes?.length) {
      const activeTheme = this.projectThemes.find(theme => theme.id === themeId);
      if (activeTheme) {
        this.projectTheme = {
          ...this.projectTheme,
          ...(activeTheme?.theme ?? {}),
          ...(activeTheme?.customTheme ?? {}),
          id: activeTheme.id,
          name: activeTheme.name
        };
        this.previousThemeId = activeTheme.id;
        this.mapThemeItems();
      }
    }
  }


  triggerNextWizardStep() {
    if (this.activeProject?.id) {
      this.store.dispatch(NextStep({ payload: { activeProjectId: this.activeProject?.id } }));
    }
  }

  deployButtonClick() {
    console.log('Deploy button clicked');
    if (!(this.deployStatus === DeployStatus.InProgress || this.deployStatus === DeployStatus.Queued)) {
      this.store.dispatch(Deploy());
    }
    this.navigateToUrl('/projects/live-preview');
  }

  syncProjectData() {
    console.log('Sync Project Data');
    if (this.activeProject?.id) {
      this.store.dispatch(new SyncPriorityRemoteData({ projectId: this.activeProject.id }));
      this.store.dispatch(new SyncSecondaryRemoteData({ projectId: this.activeProject.id })); // TODO: enable this when user navigates to studio pages
    }
  }
}
